import React, { useState } from 'react';
import { AlertCircle, Eye, EyeOff, RefreshCw, Settings } from 'lucide-react';

interface LoginFormProps {
	onSubmit: (email: string, password: string) => Promise<void>;
	loading: boolean;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSubmit, loading }) => {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [error, setError] = useState('');
	const [showPassword, setShowPassword] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError('');

		try {
			await onSubmit(email, password);
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Login failed';
			setError(errorMessage);
		}
	};

	return (
		<div className="max-w-md w-full space-y-8">
			<div className="text-center">
				<div className="mx-auto h-12 w-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
					<Settings className="h-6 w-6 text-white" />
				</div>
				<h2 className="text-3xl font-bold text-gray-900">Mergeo Admin Dashboard</h2>
				<p className="mt-2 text-sm text-gray-600">Integration Middleware Dashboard</p>
			</div>

			<form className="mt-8 space-y-6" onSubmit={handleSubmit}>
				<div className="space-y-4">
					<div>
						<label htmlFor="email" className="block text-sm font-medium text-gray-700">
							Email Address
						</label>
						<input
							id="email"
							name="email"
							type="email"
							required
							autoComplete="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
							placeholder="admin@garageplug.com"
						/>
					</div>

					<div>
						<label htmlFor="password" className="block text-sm font-medium text-gray-700">
							Password
						</label>
						<div className="relative mt-1">
							<input
								id="password"
								name="password"
								type={showPassword ? 'text' : 'password'}
								required
								autoComplete="current-password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
								placeholder="Enter your password"
							/>
							<button
								type="button"
								onClick={() => setShowPassword(!showPassword)}
								className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600">
								{showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
							</button>
						</div>
					</div>
				</div>

				{error && (
					<div className="rounded-lg bg-red-50 border border-red-200 p-4">
						<div className="flex">
							<AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
							<div className="ml-3">
								<h3 className="text-sm font-medium text-red-800">Authentication Error</h3>
								<p className="mt-1 text-sm text-red-700">{error}</p>
							</div>
						</div>
					</div>
				)}

				<button
					type="submit"
					disabled={loading}
					className="group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
					{loading ? (
						<div className="flex items-center">
							<RefreshCw className="animate-spin h-4 w-4 mr-2" />
							Signing in...
						</div>
					) : (
						'Sign In'
					)}
				</button>
			</form>
		</div>
	);
};
